import { apiFetch } from '../utils/api';

export interface Customer {
  id: string;
  erpId: number | null;
  firstName: string | null;
  lastName: string | null;
  email: string | null;
  mobileNumber: string | null;
  countryCode?: string | null;
  dateOfBirth?: string | null;
  createdAt: string;
  updatedAt: string;
  vehiclesCount?: number;
}

export interface AbandonedCartItem {
  id: string;
  customerId: string;
  orderId: string;
  productVariantId: string | null;
  productVariant?: {
    id: string;
    name?: string;
    articleNumber?: string | null;
    brand?: { id: string; name?: string | null } | null;
  } | null;
  reasons: string | null;
  reminderAfterDays: number | null;
  removedById: string | null;
  removedBy?: { id: string; firstName?: string | null; lastName?: string | null; email?: string | null } | null;
  removedAt: string;
  createdAt: string;
  updatedAt: string;
}

export interface PaginatedAbandonedCartResponse {
  data: AbandonedCartItem[];
  meta: { total: number; page: number; limit: number; totalPages: number };
}

export const fetchCustomerAbandonedCart = (
  customerId: string,
  params: { page?: number; limit?: number } = {}
): Promise<PaginatedAbandonedCartResponse> => {
  const search = new URLSearchParams();
  if (params.page) search.set('page', String(params.page));
  if (params.limit) search.set('limit', String(params.limit));
  const q = search.toString();
  return apiFetch<PaginatedAbandonedCartResponse>(
    `/customers/${customerId}/abandoned-cart${q ? `?${q}` : ''}`
  );
};

export interface PaginatedCustomersResponse {
  data: Customer[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

interface FetchCustomersParams {
  page?: number;
  limit?: number;
  search?: string;
}

export const fetchCustomers = (
  params: FetchCustomersParams = {},
): Promise<PaginatedCustomersResponse> => {
  const searchParams = new URLSearchParams();

  if (params.page) {
    searchParams.set('page', String(params.page));
  }

  if (params.limit) {
    searchParams.set('limit', String(params.limit));
  }

  if (params.search) {
    searchParams.set('search', params.search.trim());
  }

  const query = searchParams.toString();
  return apiFetch<PaginatedCustomersResponse>(
    `/customers${query ? `?${query}` : ''}`,
  );
};

interface CustomerPayload {
  firstName: string;
  lastName: string;
  email: string;
  mobileNumber: string;
  dateOfBirth: string;
  countryCode?: string;
}

export const createCustomer = (
  payload: CustomerPayload,
): Promise<Customer> =>
  apiFetch<Customer>('/customers', {
    method: 'POST',
    body: JSON.stringify(payload),
  });

export const updateCustomer = (
  id: string,
  payload: CustomerPayload,
): Promise<Customer> =>
  apiFetch<Customer>(`/customers/${id}`, {
    method: 'PATCH',
    body: JSON.stringify(payload),
  });

export const fetchCustomer = (id: string): Promise<Customer> =>
  apiFetch<Customer>(`/customers/${id}`);
